<template>
  <div>
    <div class="relative bg-white">
      <div class="absolute top-0 bottom-0 left-0 right-0 block bg-black opacity-60 z-2" />
      <img
        :src="require(`@/assets/images/auctions/hero.jpg`)"
        alt="Cattle feeding"
        class="absolute top-0 bottom-0 left-0 right-0 object-center image-cover z-1"
      />

      <div class="container-padding-x">
        <div class="relative block pt-6 mx-auto text-white bg-transparent max-w-7xl z-3 md:pt-12">
          <div class="flex flex-wrap items-start justify-between gap-x-4 md:flex-no-wrap">
            <div class="w-full md:max-w-2xl xl:pt-12">
              <img :src="require(`@/assets/images/auctions/new.png`)" alt="New" style="height: 42px !important" />

              <h1
                class="mt-4 text-5xl font-semibold leading-tight md:leading-none md:text-4xl lg:text-6xl xl:text-7xl"
              >
                Introducing SellMyLivestock Auctions
              </h1>

              <p class="w-full mt-4 leading-relaxed xl:text-xl xl:mt-6">
                The secure and reliable way to trade livestock quickly and easily online
              </p>

              <div class="flex items-center mt-6 mb-8 space-x-4 xl:mt-8">
                <UtilButton color="primary" to="/auctions/contact" class="w-full md:w-auto">
                  Request callback
                </UtilButton>
                <UtilButton color="white" @click="scrollToWhy()" class="hidden md:block">Find out more</UtilButton>
              </div>
            </div>

            <div class="self-end w-full md:pt-16 md:w-auto lg:pt-12">
              <img :src="require(`@/assets/images/auctions/screen.png`)" alt="Screenshot" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="bg-background container-padding-x">
      <div class="py-10 mx-auto md:py-24 max-w-7xl">
        <h3 class="text-3xl font-semibold text-center">Recent auction listings</h3>

        <UtilSearch :searchQuery="compSearchQuery" key="auction-listings" class="mt-8">
          <template #loading>
            <div class="grid items-center grid-cols-2 gap-6 md:grid-cols-3 lg:grid-cols-4">
              <UtilPlaceholderHitCard v-for="i in 4" :key="i" class="w-full mx-1 mb-2" />
            </div>
          </template>

          <template #default="{ listings }">
            <div class="grid items-center grid-cols-2 gap-6 md:grid-cols-3 lg:grid-cols-4">
              <div
                v-for="listing in listings"
                :key="listing.listingRef"
                class="self-stretch w-full"
                @click="auctionListingClick(listing)"
              >
                <UtilHitCard
                  type="listing"
                  :listing="listing"
                  class=""
                  :canWatch="false"
                  :showStatusTags="false"
                  shadow
                  isHomepage
                  ownerAlwaysVisible
                />
              </div>
            </div>
          </template>
        </UtilSearch>

        <!-- <div class="w-full mt-20 text-center md:mt-10">
          <UtilButton
            to="/search/livestock"
            :block="$breakpoint.xs"
            color="primary"
            class="mx-auto mt-6 font-medium"
          >
            View All Listings
          </UtilButton>
        </div> -->
      </div>
    </div>

    <!-- WHY -->
    <div class="bg-brand-darker container-padding-x">
      <div class="relative block py-6 mx-auto text-white bg-transparent max-w-7xl z-3 md:pt-12">
        <div ref="why" class="flex flex-wrap items-start justify-between gap-x-10 md:flex-no-wrap">
          <div class="w-full md:max-w-lg xl:py-12">
            <h2 class="text-3xl font-semibold leading-tight text-white md:text-2xl lg:text-4xl xl:text-5xl">
              Why should I sell through SellMyLivestock Auctions?
            </h2>

            <ul class="flex flex-col mt-8 space-y-5">
              <li class="flex items-start space-x-4">
                <img
                  :src="require(`@/assets/images/auctions/tick-circle.png`)"
                  width="16"
                  alt="-"
                  style="margin-top: 4px"
                />
                <div>
                  <p class="font-semibold list-title-light">Get the best price for your livestock</p>
                  <p class="mt-1 font-medium">
                    Maximise the value of your livestock by tapping into SellMyLivestock&lsquo;s network of
                    160,000+ farm decision makers
                  </p>
                </div>
              </li>

              <li class="flex items-start space-x-4">
                <img
                  :src="require(`@/assets/images/auctions/tick-circle.png`)"
                  width="16"
                  alt="-"
                  style="margin-top: 4px"
                />
                <div>
                  <p class="font-semibold list-title-light">Stress-free selling</p>
                  <p class="mt-1 font-medium">
                    Simply choose which livestock to sell, set your reserve price and let SellMyLivestock handle
                    the buyers - all without your livestock leaving the farm
                  </p>
                </div>
              </li>

              <li class="flex items-start space-x-4">
                <img
                  :src="require(`@/assets/images/auctions/tick-circle.png`)"
                  width="16"
                  alt="-"
                  style="margin-top: 4px"
                />
                <div>
                  <p class="font-semibold list-title-light">Secure & Reliable</p>
                  <p class="mt-1 font-medium">
                    Know you&rsquo;ll get paid with secure payments, verified buyers and a no sale, no fee
                    commission model
                  </p>
                </div>
              </li>
            </ul>

            <UtilButton color="primary" to="/auctions/contact" class="w-full mt-6 md:w-auto">
              Request callback
            </UtilButton>
          </div>

          <div class="w-full mt-12 md:w-auto xl:pt-12 md:mt-0">
            <img :src="require(`@/assets/images/auctions/why.png`)" alt="Why" />
          </div>
        </div>
      </div>
    </div>

    <!-- GETTING STARTED -->
    <div class="bg-background container-padding-x">
      <div class="relative block py-6 mx-auto bg-transparent max-w-7xl z-3 md:pt-12">
        <div class="flex flex-wrap items-start justify-between gap-x-10 md:flex-no-wrap">
          <div class="order-2 w-full mt-12 md:w-auto xl:pt-12 md:mt-0 md:order-1">
            <img :src="require(`@/assets/images/auctions/getting-started.png`)" alt="Getting started" />
          </div>

          <div class="order-1 w-full md:max-w-lg xl:py-12 md:order-2">
            <h2 class="text-3xl font-semibold leading-tight md:text-2xl lg:text-4xl xl:text-5xl">
              Getting started is simple
            </h2>

            <p class="mt-6 text-lg font-medium text-gray-700">
              If you&rsquo;re already a verified member of SellMyLivestock, all you need to do is:
            </p>

            <ul class="flex flex-col mt-8 space-y-5">
              <li class="flex items-start space-x-4">
                <img
                  :src="require(`@/assets/images/auctions/tick-circle.png`)"
                  width="16"
                  alt="-"
                  style="margin-top: 4px"
                />
                <div>
                  <p class="font-semibold text-brand-darker">Create your listing</p>
                  <p class="mt-1 font-medium">
                    Working with a dedicated account manager, you&rsquo;ll provide images and key information about
                    the livestock you want to sell.
                  </p>
                </div>
              </li>

              <li class="flex items-start space-x-4">
                <img
                  :src="require(`@/assets/images/auctions/tick-circle.png`)"
                  width="16"
                  alt="-"
                  style="margin-top: 4px"
                />
                <div>
                  <p class="font-semibold text-brand-darker">Select a date</p>
                  <p class="mt-1 font-medium">
                    Auctions start every week on Friday mornings and close the following Thursday at 7pm. Just
                    choose when you want your livestock to be auctioned.
                  </p>
                </div>
              </li>

              <li class="flex items-start space-x-4">
                <img
                  :src="require(`@/assets/images/auctions/tick-circle.png`)"
                  width="16"
                  alt="-"
                  style="margin-top: 4px"
                />
                <div>
                  <p class="font-semibold text-brand-darker">Sit back and relax</p>
                  <p class="mt-1 font-medium">
                    Once everything is ready, you watch the auction unfold from the comfort of your farm office. If
                    the auction reaches your reserve price, SellMyLivestock will take care of the payment securely.
                  </p>
                </div>
              </li>
            </ul>

            <UtilButton color="primary" to="/auctions/contact" class="w-full mt-6 md:w-auto">
              Request callback
            </UtilButton>
          </div>
        </div>
      </div>
    </div>

    <!-- WHY BUY -->
    <div class="bg-white container-padding-x">
      <div class="relative block py-6 mx-auto bg-transparent max-w-7xl z-3 md:pt-12">
        <div class="flex flex-wrap items-start justify-between gap-x-10 md:flex-no-wrap">
          <div class="w-full md:max-w-lg xl:py-12">
            <h2 class="text-3xl font-semibold leading-tight md:text-2xl lg:text-4xl xl:text-5xl">
              Why should I buy through SellMyLivestock Auctions?
            </h2>

            <ul class="flex flex-col mt-8 space-y-5">
              <li class="flex items-start space-x-4">
                <img
                  :src="require(`@/assets/images/auctions/tick-circle.png`)"
                  width="16"
                  alt="-"
                  style="margin-top: 4px"
                />
                <div>
                  <p class="font-semibold text-brand-darker">Buy with confidence</p>
                  <p class="mt-1 font-medium">
                    Buying through SellMyLivestock protects your purchase. Payments are secure and you have time to
                    raise any issues after you've received your livestock.
                  </p>
                </div>
              </li>

              <li class="flex items-start space-x-4">
                <img
                  :src="require(`@/assets/images/auctions/tick-circle.png`)"
                  width="16"
                  alt="-"
                  style="margin-top: 4px"
                />
                <div>
                  <p class="font-semibold text-brand-darker">Online-only bidding</p>
                  <p class="mt-1 font-medium">
                    Never miss an opportunity to get quality livestock. From your farm office to the tractor seat,
                    bid on the livestock you need with just a few clicks.
                  </p>
                </div>
              </li>

              <li class="flex items-start space-x-4">
                <img
                  :src="require(`@/assets/images/auctions/tick-circle.png`)"
                  width="16"
                  alt="-"
                  style="margin-top: 4px"
                />
                <div>
                  <p class="font-semibold text-brand-darker">Livestock on demand</p>
                  <p class="mt-1 font-medium">
                    Tap into the UK's largest livestock network and find the animals you need, fast.
                  </p>
                </div>
              </li>
            </ul>

            <UtilButton color="primary" to="/auctions/contact" class="w-full mt-6 md:w-auto">
              Request callback
            </UtilButton>
          </div>

          <div class="w-full mt-12 md:w-auto xl:pt-12 md:mt-0">
            <img :src="require(`@/assets/images/auctions/why-buy.png`)" alt="Getting started" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import UtilButton from '@/components/utils/UtilButton'
import UtilHitCard from '@/components/utils/UtilHitCard'
import listingStatus from '@/lib/mapping/listing-status'
import UtilSearch from '@/components/utils/search/UtilSearch'
import UtilPlaceholderHitCard from '@/components/utils/placeholders/UtilPlaceholderHitCard'

import { generateMetaTags } from '@/lib/generate-meta'
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons'

export default {
  name: 'Auctions',

  components: {
    UtilButton,
    UtilHitCard,
    UtilSearch,
    UtilPlaceholderHitCard
  },

  data() {
    return {
      faCheckCircle
    }
  },

  head: {
    title() {
      return {
        inner: 'Auctions'
      }
    },
    meta() {
      return generateMetaTags({
        title: 'Auctions',
        description: 'The online livestock auction site - Trade livestock quickly, easily and reliably',
        image: require(`@/assets/images/auctions/og.png`)
      })
    }
  },

  computed: {
    compSearchQuery() {
      return {
        commodity: 'livestock',
        types: ['A'],
        statuses: [listingStatus.LIVE, listingStatus.SOLD, listingStatus.FINISHED],
        pageLength: 12,
        includeExcludedFromSearch: 1,
        start: this.$date().subtract(5, 'month').format('YYYY-MM-DD')
      }
    }
  },

  methods: {
    auctionListingClick(listing) {
      this.$analytics.addEvent({
        category: 'listing',
        action: 'Clicked an auction listing from landing page'
      })

      this.$openWindow(`https://new.sellmylivestock.com/listings/${listing.listingRef}`)
    },

    scrollToWhy() {
      this.$nextTick(() => {
        this.$scroll.intoViewIfNeeded(this.$refs['why'])
      })
    }
  }
}
</script>

<style scoped>
.list-title-light {
  color: #f0ffdb;
}
</style>
